/* DOXCORA cart extras — decorates Horizon's native cart drawer.

   THE RULE, and the reason this file was rewritten:

   Horizon does NOT re-render its drawer by replacing DOM. It uses morph()
   (assets/morph.js), a diff that deliberately preserves things a wholesale
   replace destroys — most importantly <shopify-accelerated-checkout-cart>,
   which morph refuses to touch precisely because re-creating it breaks Shop
   Pay, and the #cart-form / #checkout pair, which are wired by the HTML
   `form="cart-form"` association across two different subtrees.

   An earlier version of this file called drawer.replaceWith(freshHtml) after
   our own cart mutations. That is what broke the checkout button and left the
   estimated total stale. We never touch Horizon's DOM again.

   Instead, when WE change the cart (upsell, gift, shipping protection) we tell
   Horizon the way its own code does: dispatch CartLinesUpdateEvent. Its
   CartItemsComponent listens for it and, when the event carries no sections,
   re-renders and morphs the section itself. That is the supported path.

   Horizon's markup we hook into (snippets/cart-drawer.liquid):
     cart-drawer-component
       > .cart-drawer__inner        <- morph target (data-hydration-key)
         > .cart-drawer__content    <- flex column
           > .cart-drawer__items
           > .cart-drawer__summary                                        */

let CartLinesUpdateEvent = null;
try {
  ({ CartLinesUpdateEvent } = await import('@shopify/events'));
} catch (e) {
  /* If the import map ever changes, we lose the refresh but break nothing. */
  console.warn('[DOXCORA] @shopify/events unavailable; cart extras will not refresh Horizon.', e);
}

const EXTRAS = '[data-dox-extras]';
const DRAWER = 'cart-drawer-component';
let busy = false;

const extras = () => document.querySelector(EXTRAS);
const drawer = () => document.querySelector(DRAWER);

/* Once a part has been moved into the drawer it no longer lives inside the
   extras holder, and Horizon's morph will happily delete it because it is not
   in the server HTML for that section. Looking it up only inside the holder
   would return null in both cases and nothing would ever come back, so we
   search the whole document and keep the last node we saw. A detached node is
   still perfectly good markup: we simply re-insert it. */
const cache = {};
function part(name) {
  const n = document.querySelector(`[data-dox-part="${name}"]`);
  if (n) {
    cache[name] = n;
    return n;
  }
  const c = cache[name];
  return c && !c.isConnected ? c : null;
}

function post(path, body) {
  return fetch(path, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify(body),
  }).then((r) => r.json());
}
const getCart = () => fetch('/cart.js', { headers: { Accept: 'application/json' } }).then((r) => r.json());

/* ---- placing our nodes inside Horizon's drawer ------------------------- */

function place() {
  const d = drawer();
  if (!d) return;
  const content = d.querySelector('.cart-drawer__content');
  const items = d.querySelector('.cart-drawer__items');
  const summary = d.querySelector('.cart-drawer__summary');
  if (!content || !items) return;

  const announce = part('announce');
  const ship = part('ship');
  const upsell = part('upsell');
  const prot = part('prot');

  /* announcement + progress go above the line items */
  if (announce && announce.parentElement !== content) content.insertBefore(announce, items);
  if (ship && ship.parentElement !== content) content.insertBefore(ship, items);
  /* upsells sit under the items, before the totals */
  if (upsell && upsell.parentElement !== content) content.insertBefore(upsell, summary || null);
  /* protection sits in the summary, just above the totals */
  if (prot && summary && prot.parentElement !== summary) summary.insertBefore(prot, summary.firstChild);

  for (const n of [announce, ship, upsell, prot]) if (n) n.hidden = false;
}

/* ---- refreshing our own section ---------------------------------------- */

/* Keep the query string: in the theme editor and previews the active theme
   travels in ?preview_theme_id=..., and without it the Section Rendering API
   answers with the PUBLISHED theme instead. */
function sectionUrl(id) {
  const base = window.location.pathname + window.location.search;
  return `${base}${base.includes('?') ? '&' : '?'}sections=${encodeURIComponent(id)}`;
}

let lastRefresh = 0;

function refreshExtras() {
  const e = extras();
  if (!e) return Promise.resolve();
  const id = e.getAttribute('data-section-id');
  return fetch(sectionUrl(id), { headers: { Accept: 'application/json' } })
    .then((r) => r.json())
    .then((j) => {
      const html = j[id] || j[Object.keys(j)[0]];
      if (!html) return;
      const fresh = new DOMParser().parseFromString(html, 'text/html').querySelector(EXTRAS);
      const cur = extras();
      if (!fresh || !cur) return;
      /* Drop the old parts wherever they ended up, then swap in the fresh
         holder so place() moves the new ones across. */
      for (const k of Object.keys(cache)) {
        cache[k]?.remove();
        delete cache[k];
      }
      cur.replaceWith(fresh);
      place();
    })
    .catch(() => {})
    .finally(() => {
      lastRefresh = Date.now();
    });
}

/* ---- telling Horizon we changed the cart -------------------------------- */

/* This replaces the old refreshHorizon(), which replaced Horizon's DOM by hand
   and broke checkout. We dispatch the same event Horizon's own components use.
   CartItemsComponent#handleCartUpdate sees no `sections` on the detail and
   calls sectionRenderer.renderSection(), which morphs — preserving the
   accelerated checkout element and the #cart-form / #checkout association.

   action is always 'update', never 'add': cart-drawer.js auto-opens the drawer
   on 'add', and we do not want a background gift reconcile to pop the drawer
   open in the customer's face. */
function notifyHorizon() {
  if (!CartLinesUpdateEvent) return Promise.resolve();
  try {
    const deferred = CartLinesUpdateEvent.createPromise();
    document.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'update',
        context: 'cart',
        lines: [],
        promise: deferred.promise,
      })
    );
    deferred.resolve({ detail: { source: 'dox-cart-extras', didError: false } });
    return Promise.resolve();
  } catch (err) {
    console.warn('[DOXCORA] could not notify Horizon of the cart change', err);
    return Promise.resolve();
  }
}

function syncAll() {
  return notifyHorizon().then(refreshExtras).then(place);
}

/* ---- watching Horizon --------------------------------------------------- */

let refreshTimer = null;

/* We watch the DOM rather than trusting an event name we did not verify. Any
   change Horizon makes — add, remove, quantity, its own morph — lands here.
   place() runs immediately so a morph that deleted our nodes is undone within
   the same frame; the section refetch is debounced and rate-limited so this
   cannot turn into a request loop with its own place() call. */
function watch() {
  const d = drawer();
  if (!d || d.__doxWatched) return;
  d.__doxWatched = true;
  new MutationObserver(() => {
    place();
    if (refreshTimer) clearTimeout(refreshTimer);
    refreshTimer = setTimeout(() => {
      refreshTimer = null;
      if (Date.now() - lastRefresh < 900) return;
      refreshExtras().then(() => {
        if (!busy) reconcileGift().then((changed) => changed && syncAll());
      });
    }, 260);
  }).observe(d, { childList: true, subtree: true });
}

/* ---- free gift ---------------------------------------------------------- */

function reconcileGift() {
  const e = extras();
  if (!e || e.getAttribute('data-gift-enabled') !== 'true') return Promise.resolve(false);
  const variant = parseInt(e.getAttribute('data-gift-variant'), 10) || 0;
  const goal = parseInt(e.getAttribute('data-gift-goal'), 10) || 0;
  if (!variant) return Promise.resolve(false);

  return getCart()
    .then((cart) => {
      let net = 0;
      let giftLine = null;
      for (const it of cart.items) {
        if (it.variant_id === variant) {
          giftLine = it;
          continue;
        }
        net += it.final_line_price;
      }
      const others = cart.items.length - (giftLine ? 1 : 0);
      const earned = others > 0 && net >= goal;

      if (earned && !giftLine) return post('/cart/add.js', { items: [{ id: variant, quantity: 1 }] }).then(() => true);
      if (!earned && giftLine) return post('/cart/change.js', { id: giftLine.key, quantity: 0 }).then(() => true);
      if (earned && giftLine && giftLine.quantity !== 1)
        return post('/cart/change.js', { id: giftLine.key, quantity: 1 }).then(() => true);
      return false;
    })
    .catch(() => false);
}

/* ---- our own actions ---------------------------------------------------- */

function act(work) {
  if (busy) return;
  busy = true;
  Promise.resolve(work())
    .then(reconcileGift)
    .then(syncAll)
    .catch((err) => console.warn('[DOXCORA] cart extras action failed', err))
    .finally(() => {
      busy = false;
    });
}

document.addEventListener('click', (ev) => {
  const t = ev.target;
  if (!(t instanceof Element)) return;

  const add = t.closest('[data-dox-add]');
  if (add) {
    ev.preventDefault();
    const vid = parseInt(add.getAttribute('data-variant'), 10) || 0;
    if (!vid) return;
    add.disabled = true;
    act(() => post('/cart/add.js', { items: [{ id: vid, quantity: 1 }] }));
    return;
  }

  const sw = t.closest('[data-dox-prot]');
  if (sw) {
    ev.preventDefault();
    const e = extras();
    const variant = e ? parseInt(e.getAttribute('data-prot-variant'), 10) || 0 : 0;
    if (!variant) return;
    const on = sw.getAttribute('aria-checked') !== 'true';
    sw.setAttribute('aria-checked', on ? 'true' : 'false');
    act(() => {
      if (on) return post('/cart/add.js', { items: [{ id: variant, quantity: 1 }] });
      return getCart().then((cart) => {
        const line = cart.items.find((i) => i.variant_id === variant);
        return line ? post('/cart/change.js', { id: line.key, quantity: 0 }) : null;
      });
    });
  }
});

function boot() {
  watch();
  place();
  /* The section was rendered with whatever the cart held at page load. If the
     customer adds something without a reload, those parts do not exist yet, so
     pull a fresh copy once on boot. */
  refreshExtras().then(() => {
    const e = extras();
    if (e && e.getAttribute('data-gift-enabled') === 'true') {
      reconcileGift().then((changed) => changed && syncAll());
    }
  });
}

if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
else boot();
